import { useCallback, useMemo, useState, type ReactNode } from 'react';

import Modal from '@cartwhl/ui/molecules/Modal';

import {
  AssetLibraryModal,
  createMediaImagePickerEmbedConfig,
  createMediaVideoPickerEmbedConfig,
  type AssetLibraryMediaSelection,
} from '../components/AssetLibrary';

export type MediaLibraryPickerOptions = {
  mediaKind: 'image' | 'video';
  mode: 'single' | 'multi';
  maxSelection?: number;
  getInitialSelection?: () => AssetLibraryMediaSelection[];
  onConfirm: (selections: AssetLibraryMediaSelection[]) => void;
};

export function useMediaLibraryPicker({
  mediaKind,
  mode,
  maxSelection,
  getInitialSelection,
  onConfirm,
}: MediaLibraryPickerOptions): {
  openPicker: () => void;
  closePicker: () => void;
  modal: ReactNode;
  isOpen: boolean;
} {
  const [isOpen, setIsOpen] = useState(false);
  const [initialSelection, setInitialSelection] = useState<
    AssetLibraryMediaSelection[]
  >([]);

  const scheduleClose = useCallback(() => {
    window.setTimeout(() => setIsOpen(false), 0);
  }, []);

  const openPicker = useCallback(() => {
    setInitialSelection([...(getInitialSelection?.() ?? [])]);
    setIsOpen(true);
  }, [getInitialSelection]);

  const embedConfig = useMemo(() => {
    const factory =
      mediaKind === 'video'
        ? createMediaVideoPickerEmbedConfig
        : createMediaImagePickerEmbedConfig;

    return factory({
      mode,
      maxSelection,
      initialSelection,
      onConfirm: (selections) => {
        onConfirm(selections);
        scheduleClose();
      },
      onCancel: scheduleClose,
    });
  }, [
    initialSelection,
    maxSelection,
    mediaKind,
    mode,
    onConfirm,
    scheduleClose,
  ]);

  const modal = (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          setIsOpen(true);
          return;
        }

        scheduleClose();
      }}
      maxWidth={1116}
      fixedHeightContent
      verticalBleed
      horizontalBleed
    >
      <AssetLibraryModal
        embedConfig={embedConfig}
        onClose={scheduleClose}
        panelStyle={{ height: '100%', width: '100%' }}
      />
    </Modal>
  );

  return {
    openPicker,
    closePicker: scheduleClose,
    modal,
    isOpen,
  };
}
